import axios from "axios";

/**
 * Why a request failed, in words a user can act on: a one-line summary plus
 * any individual reasons (each password rule not met, each invalid field...).
 */
export interface ApiErrorDetails {
  message: string;
  details: string[];
}

/**
 * Turns whatever an API call threw into an {@link ApiErrorDetails}.
 *
 * Understands the shapes the API sends back: `{ message, errors }`, a list of
 * ASP.NET Identity errors (`[{ code, description }]`), model validation
 * problems (`{ title, errors: { field: [...] } }`) and a plain string. Anything
 * else falls back to [fallback], with the status code so it can be reported.
 */
export function getApiErrorDetails(
  error: unknown,
  fallback = "Something went wrong. Please try again."
): ApiErrorDetails {
  if (!axios.isAxiosError(error)) {
    return {
      message: error instanceof Error && error.message ? error.message : fallback,
      details: [],
    };
  }

  if (!error.response) {
    return {
      message: "The server couldn't be reached. Check your connection and try again.",
      details: [],
    };
  }

  const { status, data } = error.response;

  if (status === 401) {
    return { message: "Your session has expired. Sign in again and retry.", details: [] };
  }

  if (status === 403) {
    // Some refusals say why (only an administrator can change an
    // administrator, say); anything else gets the general message.
    const reason = typeof data?.message === "string" && data.message.trim() ? data.message : null;
    return { message: reason ?? "You don't have permission to do this.", details: [] };
  }

  const asStrings = (items: unknown[]): string[] =>
    items
      .map((item: any) =>
        typeof item === "string" ? item : item?.description ?? item?.message ?? ""
      )
      .filter((text: string) => text.trim().length > 0);

  // A plain-text body is only shown when it reads like a message. A server
  // crash comes back as an exception and stack trace, which means nothing to
  // the user, so that gets a friendly message instead.
  if (typeof data === "string" && data.trim()) {
    const text = data.trim();
    const looksLikeMessage =
      status < 500 && text.length <= 300 && !text.includes("\n") && !/exception/i.test(text);

    if (looksLikeMessage) {
      return { message: text, details: [] };
    }

    const reason = exceptionReason(text);
    return {
      message: `${fallback} Something went wrong on the server. Please try again, and if it keeps happening contact support${reason ? " with the detail below" : ""}.`,
      details: reason ? [`Technical detail: ${reason}`] : [],
    };
  }

  if (Array.isArray(data)) {
    const details = asStrings(data);
    if (details.length > 0) {
      return { message: fallback, details };
    }
  }

  if (data && typeof data === "object") {
    const body = data as Record<string, any>;

    const details = Array.isArray(body.errors)
      ? asStrings(body.errors)
      : body.errors && typeof body.errors === "object"
        ? asStrings(Object.values(body.errors).flat())
        : [];

    if (typeof body.message === "string" && body.message.trim()) {
      return { message: body.message, details };
    }

    if (details.length > 0) {
      return { message: "Some of the details aren't valid:", details };
    }

    if (typeof body.title === "string" && body.title.trim()) {
      return { message: body.title, details: [] };
    }
  }

  return { message: `${fallback} (error ${status})`, details: [] };
}

/**
 * The one-line reason from a .NET exception dump, e.g. "Sequence contains more
 * than one element" from "System.InvalidOperationException: Sequence contains
 * more than one element\n   at ...". Null if the text isn't one.
 */
function exceptionReason(text: string): string | null {
  const firstLine = text.split("\n")[0].trim();
  const match = firstLine.match(/^[\w.]+Exception:\s*(.+)$/);
  const reason = match ? match[1].trim() : null;

  if (!reason) {
    return null;
  }

  return reason.length > 300 ? `${reason.slice(0, 300)}…` : reason;
}
